import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { IconButton } from '@mui/material';

import { ReactComponent as CloseIcon } from '../../../../../../../assets/icons/v2/trash.svg';
import { ReactComponent as AddIcon } from '../../../../../../../assets/icons/v2/add.svg';
import InputLabel from '../../../../../../../components/v2/InputLabel';
import MealPlanContext from '../../../../../../context/MealPlanContext';
import FirebaseContext from '../../../../../../../context/FirebaseContext';
import { LightTag } from '../../../../../../../components/Tags';
import { PrimaryButton } from '../../../../../../../components/Button/ActionButtons';

import IngredientPickerPanel from './IngredientPickerPanel';
import {
  Container,
  EmptyText,
  CategoryBlock,
  CategoryLabel,
  ChipRow,
  ShowMoreButton,
} from './styles';
import texts from './texts.json';

const CHIP_CAP = 4;

const IngredientExclusionsSection = ({ clientId, value, onChange }) => {
  const {
    loadCanonicalIngredients,
    canonicalIngredients,
    isCanonicalIngredientsLoading,
  } = useContext(MealPlanContext);

  const { firebase: { remote } } = useContext(FirebaseContext);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  // Load catalog on mount when exclusions exist so chips are categorized immediately.
  useEffect(() => {
    if (value.length > 0) {
      loadCanonicalIngredients();
    }
  }, [loadCanonicalIngredients, value.length]);

  const idToCategory = useMemo(
    () => (canonicalIngredients
      ? new Map(canonicalIngredients.map((i) => [i.id, i.category || texts.other]))
      : null),
    [canonicalIngredients],
  );

  const groupedExclusions = useMemo(() => {
    const groups = {};
    value.forEach(({ id, name }) => {
      const category = idToCategory ? (idToCategory.get(id) || texts.other) : texts.other;
      if (!groups[category]) groups[category] = [];
      groups[category].push({ id, name });
    });
    return Object.keys(groups).sort().map((cat) => ({ cat, items: groups[cat] }));
  }, [value, idToCategory]);

  const ingredients = useMemo(
    () => (canonicalIngredients || []).map((i) => ({
      id: i.id,
      name: i.name,
      category: i.category,
    })),
    [canonicalIngredients],
  );

  const handleLoad = useCallback(async () => {
    setLoadError(false);
    try {
      await loadCanonicalIngredients();
    } catch {
      setLoadError(true);
    }
  }, [loadCanonicalIngredients]);

  const handleOpenPicker = useCallback(async () => {
    setPickerOpen(true);
    await handleLoad();
  }, [handleLoad]);

  const handleRemove = useCallback((id) => {
    onChange(value.filter((e) => e.id !== id));
  }, [value, onChange]);

  const toggleCategory = useCallback((cat) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  }, []);

  const onAISearch = useCallback(async (text) => {
    const response = await remote('resolveIngredientExclusions', { text, userId: clientId });
    if (!response.ok) {
      throw new Error(response.statusText || `HTTP ${response.status}`);
    }
    return response.json();
  }, [remote, clientId]);

  return (
    <Container>
      <InputLabel>{texts.label}</InputLabel>
      {value.length === 0 ? (<EmptyText>{texts.noExclusions}</EmptyText>) : (
        groupedExclusions.map(({ cat, items }) => {
          const isExpanded = expandedCategories.has(cat);
          const visible = isExpanded ? items : items.slice(0, CHIP_CAP);
          const hiddenCount = items.length - visible.length;
          return (
            <CategoryBlock key={cat}>
              <CategoryLabel>
                {`${cat} (${items.length})`}
              </CategoryLabel>
              <ChipRow>
                {visible.map(({ id, name }) => (
                  <LightTag key={id}>
                    {name}
                    <IconButton size="small" onClick={() => handleRemove(id)}>
                      <CloseIcon />
                    </IconButton>
                  </LightTag>
                ))}
                {hiddenCount > 0 && (
                  <ShowMoreButton type="button" onClick={() => toggleCategory(cat)}>
                    {format(texts.showMore, { count: hiddenCount })}
                  </ShowMoreButton>
                )}
                {isExpanded && items.length > CHIP_CAP && (
                  <ShowMoreButton type="button" onClick={() => toggleCategory(cat)}>
                    {texts.collapse}
                  </ShowMoreButton>
                )}
              </ChipRow>
            </CategoryBlock>
          );
        }))}

      {!pickerOpen && (
        <PrimaryButton
          size="medium"
          icon={<AddIcon />}
          onClick={handleOpenPicker}
          $fitToContent
        >
          {texts.addButton}
        </PrimaryButton>
      )}

      {pickerOpen && (
        <IngredientPickerPanel
          value={value}
          onChange={onChange}
          ingredients={ingredients}
          isLoading={isCanonicalIngredientsLoading}
          loadError={loadError}
          onRetryLoad={handleLoad}
          onClose={() => setPickerOpen(false)}
          onAISearch={onAISearch}
        />
      )}
    </Container>
  );
};

IngredientExclusionsSection.propTypes = {
  clientId: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default IngredientExclusionsSection;
